import type CreativeEngine from "@cesdk/engine";
import type {DesignBlockId} from "@cesdk/cesdk-js";
import {PROFILE_IMAGE_META_KEY} from "../constants.ts";

const USER_PROFILE_IMAGE_URL_PATTERN = /uploaded\/userimages\/[^/]+\/profile_(?:crop|large|medium|small)\.jpg/i;
type CropState = {
    contentFillMode?: "Crop" | "Cover" | "Contain";
    rotation: number;
    scaleRatio: number;
    translationX: number;
    translationY: number;
};
type ImageSource = {
    uri: string;
    width: number;
    height: number;
};

const PROFILE_IMAGE_PLACEHOLDER_SIZE = 800;
const PROFILE_IMAGE_PLACEHOLDER_PATH = "/resources/images/profile.svg";
const imageSourceCache = new Map<string, Promise<ImageSource>>();

export const PROFILE_IMAGE_PLACEHOLDER_URL = resolveAppOriginUrl(PROFILE_IMAGE_PLACEHOLDER_PATH);

export async function replaceDynamicProfileImagesOnPage(
    engine: CreativeEngine,
    pageId: DesignBlockId,
    profileImageUrl: string | null | undefined,
    fallbackImageUrl: string
): Promise<void> {
    const replacementUrl = resolveImageUrl(profileImageUrl?.trim() || fallbackImageUrl);
    const replacementSource = await resolveImageSource(replacementUrl);
    const blocks = findDynamicProfileImageBlocks(engine, pageId);

    for (const blockId of blocks) {
        applyImageFill(engine, blockId, replacementSource);
    }
}

function resolveImageSource(imageUrl: string): Promise<ImageSource> {
    let cached = imageSourceCache.get(imageUrl);
    if (!cached) {
        cached = loadImageSource(imageUrl);
        imageSourceCache.set(imageUrl, cached);
    }

    return cached;
}

function loadImageSource(imageUrl: string): Promise<ImageSource> {
    if (typeof window === "undefined") {
        return Promise.resolve({
            uri: imageUrl,
            width: PROFILE_IMAGE_PLACEHOLDER_SIZE,
            height: PROFILE_IMAGE_PLACEHOLDER_SIZE,
        });
    }

    return new Promise((resolve) => {
        const image = new Image();
        image.onload = () => resolve({
            uri: imageUrl,
            width: image.naturalWidth || PROFILE_IMAGE_PLACEHOLDER_SIZE,
            height: image.naturalHeight || PROFILE_IMAGE_PLACEHOLDER_SIZE,
        });
        image.onerror = () => resolve({
            uri: imageUrl,
            width: PROFILE_IMAGE_PLACEHOLDER_SIZE,
            height: PROFILE_IMAGE_PLACEHOLDER_SIZE,
        });
        image.src = imageUrl;
    });
}

function resolveImageUrl(imageUrl: string): string {
    if (
        imageUrl.startsWith("data:") ||
        imageUrl.startsWith("blob:") ||
        /^https?:\/\//i.test(imageUrl)
    ) {
        return imageUrl;
    }

    if (typeof window === "undefined") return imageUrl;

    return new URL(imageUrl, window.location.origin).href;
}

function resolveAppOriginUrl(path: string): string {
    if (typeof window === "undefined") return path;

    return new URL(path, window.location.origin).href;
}

function findDynamicProfileImageBlocks(engine: CreativeEngine, pageId: DesignBlockId): DesignBlockId[] {
    const allGraphics = [
        ...(engine.block.findByType?.("graphic") ?? []),
        ...(engine.block.findByType?.("//ly.img.ubq/graphic") ?? []),
    ];

    return Array.from(new Set(allGraphics)).filter((blockId: DesignBlockId) => {
        try {
            return isDescendantOf(engine, blockId, pageId)
                && (
                    engine.block.getMetadata?.(blockId, PROFILE_IMAGE_META_KEY) === "1"
                    || hasUserProfileImageFill(engine, blockId)
                );
        } catch {
            return false;
        }
    });
}

function hasUserProfileImageFill(engine: CreativeEngine, blockId: DesignBlockId): boolean {
    if (!engine.block.isValid?.(blockId) || !engine.block.supportsFill?.(blockId)) return false;

    const fillId = engine.block.getFill(blockId);
    if (!fillId || !engine.block.isValid?.(fillId) || engine.block.getType?.(fillId) !== "//ly.img.ubq/fill/image") {
        return false;
    }

    try {
        const imageUri = engine.block.getString(fillId, "fill/image/imageFileURI");
        if (USER_PROFILE_IMAGE_URL_PATTERN.test(imageUri)) return true;
    } catch {
        // This fill may only use sourceSet.
    }

    try {
        return engine.block
            .getSourceSet(fillId, "fill/image/sourceSet")
            .some((source) => USER_PROFILE_IMAGE_URL_PATTERN.test(source.uri));
    } catch {
        return false;
    }
}

function applyImageFill(engine: CreativeEngine, blockId: DesignBlockId, imageSource: ImageSource): void {
    if (!engine.block.isValid?.(blockId) || !engine.block.supportsFill?.(blockId)) return;

    let fillId = engine.block.getFill(blockId);
    if (!fillId || !engine.block.isValid?.(fillId) || engine.block.getType?.(fillId) !== "//ly.img.ubq/fill/image") {
        fillId = engine.block.createFill("image");
        engine.block.setFill(blockId, fillId);
    }

    const cropState = readCropState(engine, blockId);
    engine.block.setString(fillId, "fill/image/imageFileURI", "");
    engine.block.setSourceSet(fillId, "fill/image/sourceSet", [imageSource]);
    restoreCropState(engine, blockId, cropState);
}

function readCropState(engine: CreativeEngine, blockId: DesignBlockId): CropState | null {
    if (!engine.block.supportsCrop?.(blockId)) return null;

    try {
        return {
            contentFillMode: engine.block.supportsContentFillMode?.(blockId)
                ? engine.block.getContentFillMode(blockId)
                : undefined,
            rotation: engine.block.getCropRotation(blockId),
            scaleRatio: engine.block.getCropScaleRatio(blockId),
            translationX: engine.block.getCropTranslationX(blockId),
            translationY: engine.block.getCropTranslationY(blockId),
        };
    } catch {
        return null;
    }
}

function restoreCropState(engine: CreativeEngine, blockId: DesignBlockId, cropState: CropState | null): void {
    if (!cropState || !engine.block.supportsCrop?.(blockId)) return;

    try {
        const shouldRestoreCrop = cropState.contentFillMode === "Crop";
        setProfileImageCropMode(engine, blockId, shouldRestoreCrop ? Math.max(1, cropState.scaleRatio) : 1);

        if (shouldRestoreCrop) {
            engine.block.setCropRotation(blockId, cropState.rotation);
            engine.block.setCropScaleRatio(blockId, cropState.scaleRatio);
            engine.block.setCropTranslationX(blockId, cropState.translationX);
            engine.block.setCropTranslationY(blockId, cropState.translationY);
        }
    } catch {
        // If the block cannot restore one crop field, keep the replacement image.
    }
}

function setProfileImageCropMode(engine: CreativeEngine, blockId: DesignBlockId, minScaleRatio: number): void {
    if (!engine.block.isValid?.(blockId) || !engine.block.supportsCrop?.(blockId)) return;

    try {
        if (engine.block.supportsContentFillMode?.(blockId)) {
            engine.block.setContentFillMode(blockId, "Crop");
        }
        engine.block.setCropAspectRatioLocked?.(blockId, false);
        engine.block.adjustCropToFillFrame(blockId, minScaleRatio);
    } catch {
        // Keep the image usable even if this CE.SDK version rejects a crop operation.
    }
}

function isDescendantOf(engine: CreativeEngine, node: DesignBlockId, ancestor: DesignBlockId): boolean {
    let current: DesignBlockId | null = node;

    while (current != null && engine.block.isValid?.(current)) {
        if (current === ancestor) return true;

        const parent = engine.block.getParent?.(current);
        if (parent == null || parent === current) break;
        current = parent;
    }

    return false;
}
